import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { format, isValid } from 'date-fns';
import Header from '../../Navbar/Header';
import Sidebar from '../sidebar/Sidebar';
import Pagination from '../../../component/Pagination';
import StyledDataTable from '../../../component/resuable/StyledComponent';
import LoadingSpinner from '../../../component/resuable/LoadingSpinner';
import { jotUsersListGet } from './JotSlice';
import "./Jot.css"

const pageSize = 10;

const paymentStatusOptions = ["paid", "unpaid"];

const formatDisplayDate = (date) => {
    const parsedDate = new Date(date);

    if (!date || !isValid(parsedDate)) {
        return "N/A";
    }

    return format(parsedDate, "dd/MM/yyyy");
};

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") {
        return "N/A";
    }

    return `Rs. ${amount}`;
};

const getPaymentStatusClass = (paymentStatus) => {
    const status = paymentStatus?.toLowerCase();

    if (status === "paid") {
        return "jot-user-status-paid";
    }

    if (status === "failed") {
        return "jot-user-status-failed";
    }

    return "jot-user-status-default";
};

const PaymentStatusBadge = ({ status }) => {
    const displayStatus = status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : "N/A";

    return (
        <span className={`jot-user-status-badge ${getPaymentStatusClass(status)}`}>
            {displayStatus}
        </span>
    );
};

const ExamSummary = ({ levels = [] }) => {
    if (levels.length === 0) {
        return <span className="text-secondary">N/A</span>;
    }

    const visibleLevels = levels.slice(0, 3);
    const hiddenCount = levels.length - visibleLevels.length;

    return (
        <div className="jot-user-exam-summary">
            {visibleLevels.map((level, index) => (
                <span key={`${level.exam_level}-${index}`} className="jot-user-level-chip">
                    {level.exam_level || "N/A"}
                </span>
            ))}
            {hiddenCount > 0 && (
                <span className="jot-user-more-chip">+{hiddenCount}</span>
            )}
        </div>
    );
};

const ExpandedExamDetails = ({ data }) => {
    const levels = Array.isArray(data?.levels) ? data.levels : [];

    return (
        <div className="jot-user-expanded-wrap">
            {levels.length > 0 ? levels.map((level, index) => (
                <div key={`${data.user_id}-expanded-${index}`} className="jot-user-exam-card">
                    <div className="jot-user-exam-card-head">
                        <span className="jot-user-level-chip">{level.exam_level || "N/A"}</span>
                        <PaymentStatusBadge status={level.payment_status} />
                    </div>
                    <div className="jot-user-exam-meta">
                        <span>Exam: {formatDisplayDate(level.exam_date)}</span>
                        <span>Amount: {formatCurrency(level.payment_amount)}</span>
                        <span>Result: {level.result || "N/A"}</span>
                        <span>Payment ID: {level.razorpay_payment_id || "N/A"}</span>
                    </div>
                </div>
            )) : (
                <div className="jot-user-empty-exams">No exam details available</div>
            )}
        </div>
    );
};

const JotUser = () => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState("");
    const [paymentStatus, setPaymentStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedUserId, setExpandedUserId] = useState(null);

    const { loading, jotUsersList } = useSelector(state => state.jot);
    const users = Array.isArray(jotUsersList?.data) ? jotUsersList.data : [];

    useEffect(() => {
        const params = {
            page: currentPage,
            search,
            payment_status: paymentStatus,
        };

        dispatch(jotUsersListGet(params));
    }, [dispatch, currentPage, search, paymentStatus]);

    const handlePaymentStatusChange = (e) => {
        setPaymentStatus(e.target.value);
        setCurrentPage(1);
    };

    const handleResetPaymentStatus = () => {
        setPaymentStatus("");
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const toggleExpandedUser = (userId) => {
        setExpandedUserId((currentUserId) => currentUserId === userId ? null : userId);
    };

    const columns = [
        {
            name: "Details",
            cell: (row) => (
                <button
                    type="button"
                    className="jot-user-expand-btn"
                    onClick={() => toggleExpandedUser(row.user_id)}
                    aria-label={`${expandedUserId === row.user_id ? "Hide" : "Show"} exam details`}
                    title={`${expandedUserId === row.user_id ? "Hide" : "Show"} exam details`}
                >
                    <i className={`fa ${expandedUserId === row.user_id ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true"></i>
                </button>
            ),
            button: true,
            width: "8%",
        },
        {
            name: "S.No",
            selector: (row, index) => ((currentPage - 1) * pageSize) + index + 1,
            sortable: true,
            width: "7%",
        },
        {
            name: "Username",
            cell: (row) => (
                <div className="jot-user-name-cell">
                    <span>{row.username || "N/A"}</span>
                    <small>ID: {row.user_id || "N/A"}</small>
                </div>
            ),
            sortable: true,
            width: "15%",
        },
        {
            name: "Email",
            selector: (row) => row.email || "N/A",
            sortable: true,
            width: "22%",
        },
        {
            name: "Phone",
            selector: (row) => row.phone || "N/A",
            sortable: true,
            width: "12%",
        },
        {
            name: "Exams",
            cell: (row) => <ExamSummary levels={row.levels} />,
            sortable: true,
            width: "15%",
        },
        {
            name: "Payment",
            cell: (row) => {
                const levels = Array.isArray(row.levels) ? row.levels : [];
                const paidCount = levels.filter((level) => level.payment_status?.toLowerCase() === "paid").length;
                const latestStatus = levels[0]?.payment_status;

                return (
                    <div className="jot-user-payment-cell">
                        <PaymentStatusBadge status={latestStatus} />
                        <small>{paidCount}/{levels.length} paid</small>
                    </div>
                );
            },
            sortable: true,
            width: "12%",
        },
        {
            name: "Total Amount",
            selector: (row) => {
                const levels = Array.isArray(row.levels) ? row.levels : [];
                const totalAmount = levels.reduce((total, level) => total + Number(level.payment_amount || 0), 0);

                return formatCurrency(totalAmount);
            },
            sortable: true,
            width: "9%",
        },
    ];

    return (
        <div className='container-fluid'>
            <div className='row'>
                <Header />
                <div className='col-2 p-0'>
                    <Sidebar />
                </div>
                <div className='col-10 mt-2 ps-3'>
                    <div className='card custom-card jot-user-card'>
                        {loading && <LoadingSpinner />}
                        <div className='row d-flex justify-content-end p-3'>
                            <div className="col-12 col-lg-5 p-0">
                                <div className='jot-user-toolbar'>
                                    <div className='d-flex me-md-3'>
                                        <select
                                            className="form-select text-secondary"
                                            value={paymentStatus}
                                            aria-label="Payment status filter"
                                            onChange={handlePaymentStatusChange}
                                        >
                                            <option value="">Select payment status</option>
                                            {paymentStatusOptions.map((status) => (
                                                <option key={status} value={status}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            className="table-viewcustom-button p-1 btn btn-primary btn-focus-none"
                                            onClick={handleResetPaymentStatus}
                                            type="button"
                                        >
                                            <i className="fa fa-retweet" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                    <div className='jot-user-search-wrap'>
                                        <input
                                            className='form-control search-btn'
                                            placeholder='Search'
                                            value={search}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='row p-3 pt-0'>
                            <StyledDataTable
                                columns={columns}
                                data={users}
                                highlightOnHover
                                responsive
                                paginationDefaultPage={currentPage}
                                persistTableHead={true}
                                expandableRows
                                expandableRowsComponent={ExpandedExamDetails}
                                expandableRowsHideExpander
                                expandableRowExpanded={(row) => expandedUserId === row.user_id}
                            />
                        </div>
                        <div>
                            {users.length > 0 ?
                                <Pagination
                                    className="pagination-bar"
                                    currentPage={jotUsersList?.current_page || currentPage}
                                    totalCount={jotUsersList?.no_of_records || 0}
                                    pageSize={pageSize}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                                : ''
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default JotUser
